import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ExternalLink, Plus, Search, Shield, ShieldAlert, Trash2, X } from 'lucide-react';
import { COPYRIGHT_STATUS, copyrightStatusLabel, normalizeBookTitle } from './copyrightRegistry';
import './copyright.css';

const emptyForm = { title: '', author: '', aliasesText: '', status: COPYRIGHT_STATUS.COPYRIGHT, reason: '', detectedAt: '', evidenceUrl: '', note: '' };

const timestampLabel = value => {
    if (!value) return '';
    const date = value?.toDate ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('vi-VN');
};

export function CopyrightBadge({ status, compact = false, onClick }) {
    const Icon = status === COPYRIGHT_STATUS.COPYRIGHT ? ShieldAlert : status === COPYRIGHT_STATUS.SAFE ? CheckCircle2 : Shield;
    return <button type="button" onClick={onClick} className={`copyright-badge copyright-${status} ${compact ? 'is-compact' : ''}`}><Icon size={compact ? 12 : 14} /> {copyrightStatusLabel(status)}</button>;
}

export function CopyrightDetailModal({ info, task, onClose, onAcknowledge, canManage, onEdit }) {
    if (!info?.record) return null;
    const record = info.record;
    const isCopyright = info.status === COPYRIGHT_STATUS.COPYRIGHT;
    return (
        <div className="copyright-overlay" role="dialog" aria-modal="true" aria-label="Chi tiết bản quyền">
            <div className={`copyright-detail ${isCopyright ? 'is-danger' : 'is-safe'}`}>
                <button type="button" className="copyright-close" onClick={onClose}><X size={20} /></button>
                <div className="copyright-detail-icon">{isCopyright ? <ShieldAlert size={28} /> : <CheckCircle2 size={28} />}</div>
                <span className="copyright-eyebrow">KIỂM TRA TRƯỚC KHI XỬ LÝ</span>
                <h3>{record.title || task?.bookTitle}</h3>
                <CopyrightBadge status={info.status} />
                {isCopyright && <p className="copyright-stop-copy">Không nên xử lý hoặc đăng cuốn này trước khi xem đầy đủ cảnh báo.</p>}
                <dl className="copyright-detail-grid">
                    <div><dt>Lý do</dt><dd>{record.reason || 'Chưa có lý do chi tiết'}</dd></div>
                    <div><dt>Ngày phát hiện</dt><dd>{record.detectedAt ? new Date(`${record.detectedAt}T00:00:00`).toLocaleDateString('vi-VN') : 'Chưa cập nhật'}</dd></div>
                    {record.note && <div className="is-wide"><dt>Ghi chú</dt><dd>{record.note}</dd></div>}
                    {record.author && <div className="is-wide"><dt>Tác giả</dt><dd>{record.author}</dd></div>}
                    {record.aliases?.length > 0 && <div className="is-wide"><dt>Tên khác</dt><dd>{record.aliases.join(', ')}</dd></div>}
                </dl>
                {record.evidenceUrl && <a className="copyright-evidence" href={record.evidenceUrl} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Mở nguồn/bằng chứng</a>}
                {info.legacy && <div className="copyright-legacy"><AlertTriangle size={16} /> Dữ liệu cũ chưa nằm trong Kho bản quyền tập trung.</div>}
                <div className="copyright-detail-actions">
                    {canManage && onEdit && <button type="button" className="copyright-secondary" onClick={() => onEdit(record)}>Cập nhật hồ sơ</button>}
                    {isCopyright && task && onAcknowledge && <button type="button" className="copyright-primary danger" onClick={onAcknowledge}>Tôi đã đọc cảnh báo</button>}
                    {(!isCopyright || !task || !onAcknowledge) && <button type="button" className="copyright-primary" onClick={onClose}>Đã hiểu</button>}
                </div>
            </div>
        </div>
    );
}

export default function CopyrightRegistryModal({ records = [], tasks = [], canManage, canDelete, onSave, onDelete, onClose }) {
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all');
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const usageCount = useMemo(() => {
        const counts = new Map();
        tasks.forEach(task => {
            const key = normalizeBookTitle(task.bookTitle);
            if (key) counts.set(key, (counts.get(key) || 0) + 1);
        });
        return counts;
    }, [tasks]);

    const filtered = records.filter(record => {
        const haystack = normalizeBookTitle(`${record.title || ''} ${record.author || ''} ${(record.aliases || []).join(' ')} ${record.reason || ''}`);
        return (!query || haystack.includes(normalizeBookTitle(query))) && (filter === 'all' || record.status === filter);
    });

    const startEdit = record => {
        if (String(record.id || '').startsWith('legacy:')) {
            setEditingId(null);
            setForm({ ...emptyForm, title: record.title || '', author: record.author || '', status: COPYRIGHT_STATUS.COPYRIGHT, reason: record.reason || '', note: record.note || '' });
            return;
        }
        setEditingId(record.id);
        setForm({
            title: record.title || '', author: record.author || '', aliasesText: (record.aliases || []).join('\n'),
            status: record.status || COPYRIGHT_STATUS.COPYRIGHT, reason: record.reason || '',
            detectedAt: record.detectedAt || '', evidenceUrl: record.evidenceUrl || '', note: record.note || '',
        });
    };

    const resetForm = () => { setEditingId(null); setForm(emptyForm); };
    const submit = async event => {
        event.preventDefault();
        if (!form.title.trim()) return;
        const aliases = form.aliasesText.split('\n').map(value => value.trim()).filter(Boolean);
        setSaving(true);
        try {
            await onSave(editingId, {
                title: form.title.trim(), author: form.author.trim(), normalizedTitle: normalizeBookTitle(form.title),
                aliases, normalizedAliases: aliases.map(normalizeBookTitle).filter(Boolean),
                status: form.status, reason: form.reason.trim(), detectedAt: form.detectedAt,
                evidenceUrl: form.evidenceUrl.trim(), note: form.note.trim(),
            });
            resetForm();
        } catch (error) {
            alert(error?.message || 'Không thể lưu hồ sơ bản quyền.');
        } finally { setSaving(false); }
    };

    return (
        <div className="copyright-overlay" role="dialog" aria-modal="true" aria-label="Kho bản quyền">
            <div className="copyright-registry">
                <header className="copyright-registry-header">
                    <div><span className="copyright-eyebrow">HOPETEAM SAFETY</span><h2>Kho bản quyền</h2><p>Một hồ sơ áp dụng cho mọi task trùng tên hoặc alias.</p></div>
                    <button type="button" className="copyright-close" onClick={onClose}><X size={21} /></button>
                </header>
                <div className="copyright-registry-toolbar">
                    <label><Search size={16} /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Tìm tên sách, tác giả, alias hoặc lý do..." /></label>
                    <div className="copyright-filter-tabs">
                        {[['all', 'Tất cả'], [COPYRIGHT_STATUS.COPYRIGHT, 'Bản quyền'], [COPYRIGHT_STATUS.SAFE, 'An toàn']].map(([value, label]) => <button type="button" key={value} className={filter === value ? 'active' : ''} onClick={() => setFilter(value)}>{label}</button>)}
                    </div>
                </div>
                <div className={`copyright-registry-content ${canManage ? '' : 'is-readonly'}`}>
                    <section className="copyright-record-list">
                        {filtered.length ? filtered.map(record => <article key={record.id} className={`copyright-record copyright-${record.status || COPYRIGHT_STATUS.COPYRIGHT}`}>
                            <div className="copyright-record-main">
                                <CopyrightBadge status={record.status || COPYRIGHT_STATUS.COPYRIGHT} compact />
                                <h3>{record.title}</h3>
                                {record.author && <small className="copyright-record-author">{record.author}</small>}
                                <p>{record.reason || 'Chưa có lý do chi tiết'}</p>
                                <small>{usageCount.get(record.normalizedTitle || normalizeBookTitle(record.title)) || 0} task trùng tên{record.updatedAt ? ` • cập nhật ${timestampLabel(record.updatedAt)}` : ''}</small>
                            </div>
                            {canManage && <div className="copyright-record-actions"><button type="button" onClick={() => startEdit(record)}>Sửa</button>{canDelete && <button type="button" className="danger" onClick={() => onDelete(record)}><Trash2 size={14} /></button>}</div>}
                        </article>) : <div className="copyright-empty"><Shield size={32} /><strong>Không có hồ sơ phù hợp</strong><span>Thêm cuốn đầu tiên hoặc đổi bộ lọc.</span></div>}
                    </section>
                    {canManage && <form className="copyright-editor" onSubmit={submit}>
                        <div className="copyright-editor-title"><Plus size={17} /><strong>{editingId ? 'Cập nhật hồ sơ' : 'Thêm hồ sơ'}</strong>{editingId && <button type="button" onClick={resetForm}>Tạo mới</button>}</div>
                        <label><span>Tên sách *</span><input required value={form.title} onChange={event => setForm({ ...form, title: event.target.value })} placeholder="Tên sách chuẩn" /></label>
                        <label><span>Tác giả</span><input list="task-author-options" value={form.author} onChange={event => setForm({ ...form, author: event.target.value })} placeholder="Tên tác giả" /></label>
                        <label><span>Trạng thái</span><select value={form.status} onChange={event => setForm({ ...form, status: event.target.value })}><option value={COPYRIGHT_STATUS.COPYRIGHT}>Có bản quyền</option><option value={COPYRIGHT_STATUS.SAFE}>Đã kiểm tra an toàn</option></select></label>
                        <label><span>Tên khác / alias</span><textarea rows="3" value={form.aliasesText} onChange={event => setForm({ ...form, aliasesText: event.target.value })} placeholder="Mỗi tên một dòng" /></label>
                        <label><span>Lý do</span><input value={form.reason} onChange={event => setForm({ ...form, reason: event.target.value })} placeholder="Copyright claim, takedown..." /></label>
                        <div className="copyright-editor-row"><label><span>Ngày phát hiện</span><input type="date" value={form.detectedAt} onChange={event => setForm({ ...form, detectedAt: event.target.value })} /></label><label><span>Link bằng chứng</span><input type="url" value={form.evidenceUrl} onChange={event => setForm({ ...form, evidenceUrl: event.target.value })} placeholder="https://..." /></label></div>
                        <label><span>Ghi chú</span><textarea rows="3" value={form.note} onChange={event => setForm({ ...form, note: event.target.value })} placeholder="Thông tin nội bộ cần lưu ý" /></label>
                        <button className="copyright-primary" type="submit" disabled={saving}>{saving ? 'Đang lưu...' : editingId ? 'Lưu thay đổi' : 'Thêm vào kho'}</button>
                    </form>}
                </div>
            </div>
        </div>
    );
}
